import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const SubscriptionSection = ({ profile }) => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    {
      id: 'trial',
      name: 'Trial',
      price: 0,
      credits: 10,
      features: [
        'Basic imaging recommendations',
        'iRefer guidelines access',
        'Limited to 10 recommendations',
        'Email support',
        'Single user access'
      ],
      current: profile?.tier === 'Trial'
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      credits: 100,
      features: [
        'All Trial features',
        '100 recommendations/month',
        'Priority email support',
        'PDF export functionality',
        'Basic analytics dashboard'
      ],
      current: profile?.tier === 'Starter'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 149,
      credits: 500,
      features: [
        'All Starter features',
        '500 recommendations/month',
        '24/7 phone support',
        'Advanced analytics',
        'API access',
        'Custom templates'
      ],
      current: profile?.tier === 'Professional',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 499,
      credits: 'Unlimited',
      features: [
        'All Professional features',
        'Unlimited recommendations',
        'Dedicated account manager',
        'Custom integrations',
        'Multi-institution support',
        'SLA guarantee'
      ],
      current: profile?.tier === 'Enterprise'
    }
  ];

  const handleUpgrade = (planId) => {
    setSelectedPlan(planId);
    // onUpgrade(planId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2">Subscription Management</h2>
        <p className="text-sm text-muted-foreground">
          Manage your subscription plan and view usage analytics
        </p>
      </div>
      <div className="clinical-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Current Plan</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {profile?.tier} Plan - Renews on {profile?.renewalDate}
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
            <Icon name="Crown" size={20} color="var(--color-primary)" />
            <span className="text-lg font-semibold text-primary">{profile?.tier}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Coins" size={18} color="var(--color-accent)" />
              <span className="text-xs text-muted-foreground">Credits Used</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">
              {profile?.creditsUsed} / {profile?.creditLimit}
            </p>
            <div className="mt-2 h-2 bg-background rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${(profile?.creditsUsed / profile?.creditLimit) * 100}%` }}
              />
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={18} color="var(--color-primary)" />
              <span className="text-xs text-muted-foreground">Days Remaining</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{profile?.daysRemaining}</p>
            <p className="text-xs text-muted-foreground mt-1">Until next billing cycle</p>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="TrendingUp" size={18} color="var(--color-success)" />
              <span className="text-xs text-muted-foreground">Monthly Usage</span>
            </div>
            <p className="text-2xl font-semibold text-foreground">{profile?.monthlyUsage}</p>
            <p className="text-xs text-muted-foreground mt-1">Recommendations this month</p>
          </div>
        </div>

        <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          <div>
            <p className="text-sm font-medium text-warning">Credit Usage Alert</p>
            <p className="text-xs text-muted-foreground mt-1">
              You've used {Math.round((profile?.creditsUsed / profile?.creditLimit) * 100)}% of your monthly credits. Consider upgrading for more capacity.
            </p>
          </div>
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Available Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans?.map((plan) => (
            <div
              key={plan?.id}
              className={`clinical-card p-6 relative ${
                plan?.popular ? 'border-2 border-primary' : ''
              } ${plan?.current ? 'bg-primary/5' : ''}`}
            >
              {plan?.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  Most Popular
                </div>
              )}
              {plan?.current && (
                <div className="absolute top-4 right-4">
                  <div className="status-badge status-badge-success">
                    <Icon name="Check" size={12} />
                    <span className="ml-1">Current</span>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h4 className="text-lg font-semibold text-foreground">{plan?.name}</h4>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-3xl font-bold text-foreground">${plan?.price}</span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {typeof plan?.credits === 'number' ? `${plan?.credits} credits` : plan?.credits}
                </p>
              </div>

              <ul className="space-y-2 mb-6">
                {plan?.features?.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs text-foreground">
                    <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={plan?.current ? 'outline' : plan?.popular ? 'default' : 'secondary'}
                fullWidth
                disabled={plan?.current}
                onClick={() => handleUpgrade(plan?.id)}
              >
                {plan?.current ? 'Current Plan' : 'Upgrade'}
              </Button>
            </div>
          ))}
        </div>
      </div>
      <div className="clinical-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Billing History</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {profile?.billingHistory?.map((item) => (
                <tr key={item?.id}>
                  <td className="text-foreground">{item?.date}</td>
                  <td className="text-foreground">{item?.description}</td>
                  <td className="text-foreground font-mono">${item?.amount}</td>
                  <td>
                    <span className={`status-badge ${
                      item?.status === 'Paid' ? 'status-badge-success' : 'status-badge-warning'
                    }`}>
                      {item?.status}
                    </span>
                  </td>
                  <td>
                    <Button variant="ghost" size="sm" iconName="Download">
                      Download
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSection;